import { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tag, RotateCcw, AlertTriangle, Loader2, Sparkles } from "lucide-react";

const BIG7 = [
  { k: "energy_kj", label: "Energie", unit: "kJ", indent: false },
  { k: "energy_kcal", label: "Energie", unit: "kcal", indent: false },
  { k: "fat", label: "Tuky", unit: "g", indent: false },
  { k: "saturated", label: "z toho nasycené mastné kyseliny", unit: "g", indent: true },
  { k: "carbs", label: "Sacharidy", unit: "g", indent: false },
  { k: "sugars", label: "z toho cukry", unit: "g", indent: true },
  { k: "protein", label: "Bílkoviny", unit: "g", indent: false },
  { k: "salt", label: "Sůl", unit: "g", indent: false },
];

export function ProductLabelPanel({ calcId, refreshKey }) {
  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/calculations/${calcId}/label`)
      .then((r) => setLabel(r.data))
      .finally(() => setLoading(false));
  }, [calcId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const big7 = label?.big7_per_100g || {};
  const composition = (label?.composition || []).filter((c) => c.weighable);
  const nonWeighable = (label?.composition || []).filter((c) => !c.weighable);

  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="product-label-panel">
      <div className="px-6 py-3.5 border-b border-border flex items-center justify-between gap-3 bg-muted/30">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-primary" />
          <h3 className="font-display font-medium">Etiketa výrobku</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="label-refresh" className="text-muted-foreground">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Obnovit
        </Button>
      </div>

      <div className="px-6 py-5">
        <p className="text-xs text-muted-foreground mb-4">
          Etiketa vychází z <strong>uložené</strong> kalkulace a výživových hodnot surovin. Po úpravě množství nejprve uložte, poté obnovte.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Počítám…
          </div>
        ) : !label ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Etiketu se nepodařilo načíst.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">
                Výživové hodnoty na 100 g výrobku
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                {BIG7.map((f) => (
                  <div key={f.k} className="flex justify-between items-center px-4 py-2 border-b border-border last:border-0 text-sm"
                    data-testid={`label-big7-${f.k}`}>
                    <span className={f.indent ? "pl-4 text-muted-foreground" : "font-medium"}>{f.label}</span>
                    <span className="num">{fmt(big7[f.k] || 0, f.k.startsWith("energy") ? 0 : 1)} {f.unit}</span>
                  </div>
                ))}
              </div>
              {label.coverage_grams < label.total_grams && (
                <p className="text-[11px] text-secondary mt-2 flex items-start gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  Hodnoty pokrývají {fmt(label.coverage_grams, 0)} g z {fmt(label.total_grams, 0)} g. Doplňte výživové hodnoty chybějících surovin pro přesnou etiketu.
                </p>
              )}
              {label.auto_used && (
                <p className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1" data-testid="label-auto-hint">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5 text-secondary" />
                  Označené (~) suroviny používají automatický odhad z vestavěného číselníku. Pro potvrzení je uložte v Číselníku surovin → Výživové hodnoty.
                </p>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">
                  Složení (sestupně dle podílu)
                </div>
                <div className="text-sm leading-relaxed" data-testid="label-composition">
                  {composition.length === 0 ? (
                    <span className="text-muted-foreground">Žádné vážitelné suroviny (kg/l/g/ml).</span>
                  ) : (
                    composition.map((c, i) => (
                      <span key={i} className={!c.has_nutrition ? "text-muted-foreground" : ""}>
                        {c.source === "auto" && <span className="text-secondary" title="Automatický odhad z číselníku">~</span>}
                        {c.name}
                        {c.percent != null && <span className="text-muted-foreground"> ({fmt(c.percent, 1)} %)</span>}
                        {i < composition.length - 1 ? ", " : ""}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">Alergeny</div>
                {label.allergens.length === 0 ? (
                  <span className="text-sm text-muted-foreground">Žádné evidované alergeny.</span>
                ) : (
                  <p className="text-sm" data-testid="label-allergens">
                    Obsahuje:{" "}
                    {label.allergens.map((a, i) => (
                      <span key={a.id}>
                        <strong data-testid={`label-allergen-${a.id}`}>{a.name}</strong>
                        {i < label.allergens.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </p>
                )}
              </div>

              {(label.missing_nutrition.length > 0 || nonWeighable.length > 0) && (
                <div className="bg-secondary/5 border border-secondary/20 rounded-lg px-3 py-2.5 space-y-1.5">
                  {label.missing_nutrition.length > 0 && (
                    <div className="text-[11px] text-secondary flex items-start gap-1" data-testid="label-missing">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>Bez výživových hodnot: {label.missing_nutrition.join(", ")}</span>
                    </div>
                  )}
                  {nonWeighable.length > 0 && (
                    <div className="text-[11px] text-muted-foreground">
                      Nezahrnuto do % (jednotka ks): {nonWeighable.map((c) => c.name).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
